import { useState, useCallback, useMemo, memo, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import { styled } from "@mui/material/styles";
import MuiDivider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSelect from "components/SoftSelect";
import SoftButton from "components/SoftButton";
import Comments from "layouts/Flow Designer/components/FlowPropertiesPanel/components/Comments";
import CustomDrawerPanel from "layouts/Flow Designer/components/CustomDrawerPanel";
import TriggerDeleteDialog from "../Titlebar/components/TitlebarActions/TitlebarTriggerList/TriggerDeleteDialog";
import { openTriggerDeleteDialog } from "redux/actions/flowDesigner/triggersActionCreator";
import {
  addFlowTrigger,
  deleteFlowTrigger
} from "redux/actions/flowDesigner/triggersActionCreator";
import allTriggersSchema from "layouts/Flow Designer/schemas/triggers";
import { TRIGGER_TYPES, TRIGGER_ICON_BY_TYPE } from "constants/flowDesigner";
import TriggerPropertiesPanel from "../TriggerPropertiesPanel";
import { TabContextApi } from "layouts/Flow Designer/context";
import useAllowEditWorkflow from "hooks/useAllowEditWorkflow";
import { useDetermineShareFlowUserAccessType } from "hooks/useDetermineShareFlowUserAccessType";
import TriggerMenuItem from "layouts/Flow Designer/components/TriggerMenuItem";

const Divider = styled(MuiDivider)(({ theme }) => ({
  margin: theme.spacing(2, 0)
}));

const StartNodePanel = ({ setNodeStartTriggerListOpen = Function() }) => {
  const [mainTabValue, setMainTabValue] = useState(0);
  const [newTriggerType, setNewTriggerType] = useState("");
  const dispatch = useDispatch();
  const { showStartNodePanel } = useSelector((store) => store?.designer?.componentPropsPanel);
  const { flowTriggers, showTriggerPropertiesPanel, selectedTrigger } = useSelector(
    (state) => state?.designer?.triggers
  );
  const { autoSaveFlow } = useContext(TabContextApi);
  const { currentSavedFlow } = useSelector((store) => store?.designer?.flows);
  const isFlowActive = currentSavedFlow?.shared_status === "active";
  const { allowEditWorkflow } = useAllowEditWorkflow();
  const { isReadOnlyAccess } = useDetermineShareFlowUserAccessType(currentSavedFlow?.id);

const availableTriggers = useMemo(() => {
  const addedTriggerTypes = flowTriggers.map((trigger) => trigger.type); // walang filter exception
  return allTriggersSchema
    ?.filter(
      (trigger) =>
        trigger.type !== TRIGGER_TYPES.BLUE_PRISM_DELTA &&
        !addedTriggerTypes.includes(trigger.type)
    )
    .sort((a, b) => a.label.localeCompare(b.label));
}, [flowTriggers]);

  const handleChangeTab = useCallback((event, newValue) => {
    setMainTabValue(newValue);
  }, []);

const handleAddTrigger = async () => {
  const triggerToAdd = availableTriggers.find((trigger) => trigger.type === newTriggerType);
  if (triggerToAdd) {
    // Add this guard:
    const alreadyAdded = flowTriggers.some((t) => t.type === triggerToAdd.type);
    if (alreadyAdded) return;
    dispatch(addFlowTrigger(triggerToAdd));
    autoSaveFlow();
    setNewTriggerType("");
  }
};

  const handleSelectChange = useCallback((event) => {
    const value = event?.target?.value ?? event?.value ?? "";
    setNewTriggerType(value);
  }, []);

  return (
    <CustomDrawerPanel
      className="no-translate trigger-props-panel"
      open={showStartNodePanel}
      title="Start"
    >
      <>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            backgroundColor: "transparent"
          }}
        >
          <Tabs
            value={mainTabValue}
            onChange={handleChangeTab}
            aria-label="start node tabs"
            sx={{
              backgroundColor: "transparent !important",
              "& .MuiTabs-root": { backgroundColor: "transparent !important" },
              "& .MuiTab-root": { backgroundColor: "transparent !important" },
              "& .MuiTabs-indicator": { backgroundColor: "transparent !important" }
            }}
          >
            <Tab
              label="TRIGGERS"
              sx={{
                backgroundColor: "transparent !important",
                minHeight: 48,
                "&.Mui-selected": {
                  backgroundColor: "transparent !important",
                  color: "#7443FF"
                },
                "&:hover": { backgroundColor: "transparent !important" }
              }}
            />
            <Tab
              label="COMMENTS"
              sx={{
                backgroundColor: "transparent !important",
                minHeight: 48,
                "&.Mui-selected": {
                  backgroundColor: "transparent !important",
                  color: "#7443FF"
                },
                "&:hover": { backgroundColor: "transparent !important" }
              }}
            />
          </Tabs>
        </Box>

        {mainTabValue === 0 && (
          <SoftBox p={2} sx={{ width: "100%" }}>
            {flowTriggers?.sortAlphabetical("label").map((trigger) => {
              return (
                <TriggerMenuItem
                 key={trigger.type}
                  allowDescription
                  isTitlebarAction
                  trigger={trigger}
                  customStyles={{ fontSize: "22px", minWidth: "40px" }}
                  setNodeStartTriggerListOpen={setNodeStartTriggerListOpen}
                  allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)}
                />
              );
            })}

            <Divider />

            <SoftTypography variant="subtitle2" fontWeight="bold" mb={1}>
              Add Trigger
            </SoftTypography>
            <SoftBox
              display="flex"
              alignItems="center"
              gap={2}
              sx={{
                width: "100%",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" }
              }}
            >
              <SoftBox sx={{ flex: 1, minWidth: 0 }}>
                <SoftSelect
                  value={availableTriggers.find((trigger) => trigger.type === newTriggerType) || ""}
                  onChange={handleSelectChange}
                  placeholder="Select a trigger"
                  options={availableTriggers.map((trigger) => ({
                    value: trigger.type || "",
                    label: (
                      <SoftBox display="flex" alignItems="center" gap={1}>
                        {TRIGGER_ICON_BY_TYPE[trigger.type]}
                        {trigger.label}
                      </SoftBox>
                    )
                  }))}
                  fullWidth
                  MenuProps={{
                    PaperProps: { sx: { minWidth: 500, maxWidth: "none" } }
                  }}
                  isDisabled={isFlowActive || !allowEditWorkflow(isReadOnlyAccess)}
                />
              </SoftBox>
              <SoftButton
                variant="gradient"
                color="primary"
                onClick={handleAddTrigger}
                disabled={!newTriggerType}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                Add
              </SoftButton>
            </SoftBox>
          </SoftBox>
        )}

        {mainTabValue === 1 && <Comments node_id="start-node" />}

        <TriggerDeleteDialog
          open={!!showTriggerPropertiesPanel}
          triggerId={selectedTrigger?.id}
          onClose={() => dispatch(openTriggerDeleteDialog(false))}
          onConfirm={() => () => dispatch(deleteFlowTrigger(selectedTrigger?.id))}
        />
        {showTriggerPropertiesPanel && <TriggerPropertiesPanel />}
      </>
    </CustomDrawerPanel>
  );
};

export default memo(StartNodePanel);
